/**
 * Multi-step form controller for warranty claims
 */
class WarrantyClaimsFormController {
    constructor(validator, fileManager, apiEndpoints, customerId, categoriesMapping) {
        this.validator = validator;
        this.fileManager = fileManager;
        this.apiEndpoints = apiEndpoints;
        this.customerId = customerId;
        this.categoriesMapping = categoriesMapping;
        this.form = document.getElementById('product-registration-form');
        this.steps = document.querySelectorAll('.form-step');
        this.progressSteps = document.querySelectorAll('.progress-step');
        this.nextButtons = document.querySelectorAll('.next-step');
        this.prevButtons = document.querySelectorAll('.prev-step');
        this.isSubmitting = false;
    }

    clearAndAppend(parent, ...children) {
        if (!parent) return;
        parent.replaceChildren(...children.filter(Boolean));
    }

    createEl(tag, className = null, text = null) {
        const el = document.createElement(tag);
        if (className) el.className = className;
        if (text !== null && text !== undefined) el.textContent = String(text);
        return el;
    }

    createReviewItem(label, value) {
        const item = this.createEl('div', 'review-item');
        item.append(
            this.createEl('span', 'review-label', `${label}:`),
            this.createEl('span', 'review-value', value)
        );
        return item;
    }

    createSpinner() {
        return this.createEl('span', 'spinner');
    }

    initialize() {
        // Generate the token before wiring the submit handler so a submission can
        // never fire without one.
        this.initializeSubmissionToken();
        this.attachEventListeners();
        this.initializeDatePicker();
        this.initializeCountrySelector();
        this.initializeCategorySelector();
    }

    initializeSubmissionToken() {
        let input = this.form.querySelector('input[name="submission-token"]');
        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'submission-token';
            this.form.appendChild(input);
        }
        input.value = this.generateSubmissionToken();
    }

    // Resilient UUID: crypto.randomUUID -> crypto.getRandomValues (v4) -> Math.random.
    // Never throws, so the form always has a token even on older browsers/contexts
    // where crypto.randomUUID is unavailable.
    generateSubmissionToken() {
        try {
            if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
                return crypto.randomUUID();
            }
            if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
                const bytes = crypto.getRandomValues(new Uint8Array(16));
                bytes[6] = (bytes[6] & 0x0f) | 0x40;
                bytes[8] = (bytes[8] & 0x3f) | 0x80;
                const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
                return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
            }
        } catch (_) { /* fall through to the non-crypto fallback */ }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
    }

    attachEventListeners() {
        this.nextButtons.forEach(button => {
            button.addEventListener('click', () => this.handleNextStep(button));
        });

        this.prevButtons.forEach(button => {
            button.addEventListener('click', () => this.handlePrevStep(button));
        });

        this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
        this.form.addEventListener('input', (e) => this.handleLiveValidation(e));
        this.form.addEventListener('change', (e) => this.handleLiveValidation(e));
    }

    initializeDatePicker() {
        const purchaseDateInput = document.getElementById('purchase-date');
        if (!purchaseDateInput) return;

        purchaseDateInput.addEventListener('input', (e) => {
            e.target.value = DateUtils.formatWithSlashes(e.target.value);

            if (e.target.value.length === 10) {
                this.validator.applyDateFieldFeedback(e.target);
            } else {
                this.validator.clearFieldError(e.target);
                this.validator.clearFieldWarning(e.target);
            }
        });

        purchaseDateInput.addEventListener('blur', (e) => {
            this.validator.applyDateFieldFeedback(e.target);
        });
    }

    initializeCountrySelector() {
        const countrySelector = document.getElementById('country');
        const stateProvinceSelector = document.getElementById('state-province');

        if (!countrySelector || !stateProvinceSelector) return;

        countrySelector.addEventListener('change', () => {
            const selectedCountry = countrySelector.value;
            AddressDataManager.populateStates(selectedCountry, stateProvinceSelector);
        });

        // Ensure states are populated if the country is pre-filled (e.g., by browser autocomplete)
        if (countrySelector.value) {
            AddressDataManager.populateStates(countrySelector.value, stateProvinceSelector);
        }
    }

    initializeCategorySelector() {
        const productCategorySelector = document.getElementById('product-category');
        const defectivePartSelector = document.getElementById('defective-part');
        const defectivePartWrapper = document.querySelector('.defective-part-wrap');

        if (!productCategorySelector || !defectivePartSelector || !defectivePartWrapper) return;

        productCategorySelector.addEventListener('change', () => {
            const selectedCategory = productCategorySelector.value;
            
            if (selectedCategory === '') {
                defectivePartWrapper.style.display = 'none';
            } else {
                defectivePartWrapper.style.display = 'block';
                const selectedParts = this.categoriesMapping.find(
                    category => category.title === selectedCategory
                );
                
                if (selectedParts && selectedParts.parts) {
                    defectivePartSelector.replaceChildren();
                    defectivePartSelector.appendChild(this.createEl('option', null, 'Please select'));
                    defectivePartSelector.options[0].value = '';

                    selectedParts.parts.forEach(part => {
                        const opt = document.createElement('option');
                        opt.value = part.code;
                        opt.textContent = part.title;
                        defectivePartSelector.appendChild(opt);
                    });
                } else {
                    defectivePartSelector.replaceChildren();
                    defectivePartSelector.appendChild(this.createEl('option', null, 'Please select'));
                    defectivePartSelector.options[0].value = '';
                }
            }
        });
    }

    handleNextStep(button) {
        const currentStep = parseInt(button.closest('.form-step').dataset.step);
        const nextStep = parseInt(button.dataset.next);

        if (this.validateStep(currentStep)) {
            this.showStep(nextStep);
            this.updateProgress(nextStep);
        }
    }

    handlePrevStep(button) {
        const prevStep = parseInt(button.dataset.prev);
        this.showStep(prevStep);
        this.updateProgress(prevStep);
    }

    handleLiveValidation(e) {
        const target = e.target;
        if (!target || !target.matches('input, textarea, select')) return;
        if (target.id === 'purchase-date') return;

        const formGroup = target.closest('.form-group') || target.parentElement;
        if (!formGroup) return;

        const hasExistingError = !!formGroup.querySelector('.error-message');
        if (!hasExistingError) return;

        const message = this.validator.validateField(target);
        if (message) {
            this.validator.showFieldError(target, message);
        } else {
            this.validator.clearFieldError(target);
        }
    }

    validateStep(stepNumber) {
        const currentStep = document.querySelector(`.form-step[data-step="${stepNumber}"]`);
        const fields = currentStep.querySelectorAll('input, select, textarea');
        let isValid = true;
        let firstInvalidField = null;

        fields.forEach(field => {
            const message = field.id === 'purchase-date'
                ? this.validator.applyDateFieldFeedback(field)
                : this.validator.validateField(field);

            if (message) {
                if (field.id !== 'purchase-date') {
                    this.validator.showFieldError(field, message);
                }
                if (!firstInvalidField) firstInvalidField = field;
                isValid = false;
            } else if (field.id !== 'purchase-date') {
                this.validator.clearFieldError(field);
            }
        });

        if (!isValid && firstInvalidField) {
            try {
                firstInvalidField.focus({ preventScroll: true });
            } catch (e) {
                firstInvalidField.focus();
            }
            firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return isValid;
    }

    showStep(stepNumber) {
        this.steps.forEach(step => step.classList.remove('active'));

        const targetSteps = document.querySelectorAll(`.form-step[data-step="${stepNumber}"]`);
        targetSteps.forEach(step => step.classList.add('active'));

        if (stepNumber === 4) {
            this.updateReviewSection();
        }

        // Scroll to the top of the warranty-claims form
        const warrantyClaims = document.querySelector('.form-progress');
        if (warrantyClaims) {
            warrantyClaims.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    updateProgress(currentStep) {
        this.progressSteps.forEach((step, index) => {
            const stepNumber = index + 1;
            step.classList.remove('active', 'completed');

            if (stepNumber < currentStep) {
                step.classList.add('completed');
            } else if (stepNumber === currentStep) {
                step.classList.add('active');
            }
        });
    }

    updateReviewSection() {
        this.updateProductInfo();
        this.updateDocumentationInfo();
        this.updateContactInfo();
        this.updateAddressInfo();
    }

    updateProductInfo() {
        const productInfo = document.getElementById('review-product-info');
        if (!productInfo) return;

        const fields = [
            { label: 'Category', id: 'product-category' },
            { label: 'Subcategory', id: 'defective-part' },
            { label: 'Description', id: 'product-description' },
            { label: 'Purchase Date', id: 'purchase-date' },
            { label: 'Price', id: 'product-price', prefix: '$' },
            { label: 'Store', id: 'purchase-store' },
            { label: 'Serial Number', id: 'serial-number' }
        ];

        const items = fields.map(field => {
            const el = document.getElementById(field.id);
            const value = el?.value || 'Not provided';
            const displayValue = (() => {
                // Show labels for selects (option text) rather than internal codes.
                if ((field.id === 'product-category' || field.id === 'defective-part') && el && el.tagName === 'SELECT') {
                    const selectedOption = el.selectedOptions?.[0];
                    const optionText = selectedOption?.textContent?.trim();
                    return optionText || value;
                }
                return field.prefix ? `${field.prefix}${value}` : value;
            })();
            return this.createReviewItem(field.label, displayValue);
        });

        this.clearAndAppend(productInfo, ...items);
    }

    updateDocumentationInfo() {
        const docsInfo = document.getElementById('review-docs-info');
        if (!docsInfo) return;

        const nodes = [];

        // Check each file type
        Object.keys(this.fileManager.fileUploads).forEach(type => {
            const fileData = this.fileManager.fileUploads[type];
            const typeLabel = type === 'receipt' ? 'Order Receipt Confirmation' : 
                            type === 'damage' ? 'Damage Photos' : 'Serial Number Photos';

            if (fileData.files.length === 0) {
                nodes.push(this.createReviewItem(typeLabel, 'No files uploaded'));
            } else {
                nodes.push(this.createReviewItem(typeLabel, `${fileData.files.length} file(s)`));

                fileData.files.forEach(file => {
                    if (file.type && file.type.startsWith('image/')) {
                        const url = URL.createObjectURL(file);
                        const item = this.createEl('div', 'review-file-item');
                        const thumb = this.createEl('div', 'review-file-thumb');
                        const img = document.createElement('img');
                        img.src = url;
                        img.alt = file.name;
                        img.addEventListener('load', () => URL.revokeObjectURL(img.src));
                        thumb.appendChild(img);
                        item.append(thumb, this.createEl('span', 'review-file-name', file.name));
                        nodes.push(item);
                    } else {
                        nodes.push(this.createReviewItem('File', file.name));
                    }
                });
            }
        });

        this.clearAndAppend(docsInfo, ...nodes);
    }

    updateContactInfo() {
        const personalInfo = document.getElementById('review-personal-info');
        if (!personalInfo) return;

        const fields = [
            { label: 'First Name', id: 'first-name' },
            { label: 'Last Name', id: 'last-name' },
            { label: 'Phone', id: 'phone' },
            { label: 'Email', id: 'email' },
            { label: 'Newsletter Subscription', id: 'newsletter-opt-in', type: 'checkbox' }
        ];

        const items = fields.map(field => {
            const el = document.getElementById(field.id);
            const value = field.type === 'checkbox'
                ? (el?.checked ? 'Yes' : 'No')
                : (el?.value || 'Not provided');
            return this.createReviewItem(field.label, value);
        });

        this.clearAndAppend(personalInfo, ...items);
    }

    updateAddressInfo() {
        const addressInfo = document.getElementById('review-address');
        if (!addressInfo) return;

        const countryValue = document.getElementById('country')?.value;
        const countryDisplay =
            !countryValue || countryValue.trim() === ''
                ? 'Not provided'
                : countryValue === 'US'
                    ? 'United States'
                    : countryValue === 'CA'
                        ? 'Canada'
                        : countryValue;

        const stateProvinceEl = document.getElementById('state-province');
        const stateProvinceValue = stateProvinceEl?.value?.trim();
        const stateProvinceDisplay = (() => {
            if (!stateProvinceValue) return 'Not provided';
            if (stateProvinceEl && stateProvinceEl.tagName === 'SELECT') {
                const selectedOption = stateProvinceEl.selectedOptions?.[0];
                const optionText = selectedOption?.textContent?.trim();
                return optionText || stateProvinceValue;
            }
            return stateProvinceValue;
        })();

        const items = [
            this.createReviewItem('Street Address', document.getElementById('street-address')?.value || 'Not provided'),
            this.createReviewItem('City', document.getElementById('city')?.value || 'Not provided'),
            this.createReviewItem('State/Province', stateProvinceDisplay),
            this.createReviewItem('Zip/Postal Code', document.getElementById('zip-postal')?.value || 'Not provided'),
            this.createReviewItem('Country', countryDisplay),
        ];

        this.clearAndAppend(addressInfo, ...items);
    }

    async handleFormSubmit(e) {
        e.preventDefault();

        if (this.isSubmitting) return;
        this.isSubmitting = true;

        const submitBtn = e.target.querySelector('.btn-form-submit');
        
        // Prevent double submission - disable button and change type to prevent form resubmission
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.type = 'button';
            this.setButtonState(submitBtn, true, [this.createSpinner(), this.createEl('span', null, 'Submitting...')]);
        }

        try {
            const formData = this.collectFormData();

            // Upload all three types of files
            const receiptUpload = await this.uploadFile('receipt');
            const damageUpload = await this.uploadFile('damage');
            const serialUpload = await this.uploadFile('serial');
            
            await this.registerWarrantyClaim(
                formData,
                receiptUpload.fileName,
                damageUpload.fileName,
                serialUpload.fileName
            );

            if (document.getElementById('newsletter-opt-in')?.checked) {
                await this.subscribeToShopifyNewsletter(formData.email);
            }

            this.showFormSuccess();

        } catch (error) {
            console.error('Submission error:', error);
            this.isSubmitting = false;
            this.showFinalStep(false, error.message);
        }
    }

    async subscribeToShopifyNewsletter(email) {
        if (!email || email === 'Not provided') return;
        const body = new URLSearchParams();
        body.append('form_type', 'customer');
        body.append('utf8', '✓');
        body.append('contact[email]', email);
        body.append('contact[tags]', 'newsletter');
        await fetch('/contact', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString()
        });
    }

    showFormSuccess() {
        const wrapper = document.querySelector('.warranty-claims');
        const body = wrapper?.querySelector('.warranty-claims__body');
        const success = wrapper?.querySelector('.warranty-claims__success');

        if (body) body.hidden = true;
        if (success) {
            success.hidden = false;
            success.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    showFinalStep(isSuccess, errorMessage = null) {
        if (isSuccess) {
            this.showFormSuccess();
            return;
        }

        // Show step 5 for errors
        this.showStep(5);
        
        const step4Progress = document.querySelector('.progress-step[data-step="4"]');
        if (step4Progress) {
            step4Progress.classList.remove('active');
            step4Progress.classList.add('failed');
        }
        
        const resultContainer = document.querySelector('.form-step[data-step="5"] .result-message-container');
        if (resultContainer) {
            this.renderErrorMessage(resultContainer, errorMessage);
        }

        const submitBtn = this.form.querySelector('.btn-form-submit');
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.type = 'submit';
            this.setButtonState(submitBtn, false, this.createEl('span', null, 'Submit Warranty'));
        }
    }

    renderErrorMessage(container, errorMessage) {
        const wrap = this.createEl('div', 'error-message');
        const safeMessage = (errorMessage && typeof errorMessage === 'string' && errorMessage.trim())
            ? errorMessage.trim()
            : 'An unexpected error occurred';
        const errorDetails = this.createEl('p', 'error-details');
        errorDetails.textContent = `Error: ${safeMessage}`;

        wrap.append(
            this.createEl('div', 'error-icon', '✗'),
            this.createEl('h3', null, 'Warranty Registration Failed'),
            this.createEl('p', null, 'Please refresh the page and try again or contact support.'),
            errorDetails
        );
        this.clearAndAppend(container, wrap);
    }

    collectFormData() {
        return {
            description: document.getElementById('product-description')?.value || 'Not provided',
            customer_id: this.customerId,
            purchase_date: DateUtils.convertToISOFormat(document.getElementById('purchase-date')?.value || ''),
            store: document.getElementById('purchase-store')?.value || 'Not provided',
            serialno: document.getElementById('serial-number')?.value || 'Not provided',
            price: document.getElementById('product-price')?.value || 'Not provided',
            serviceordertype: document.getElementById('defective-part')?.value || 'Not provided',
            first_name: document.getElementById('first-name')?.value || 'Not provided',
            last_name: document.getElementById('last-name')?.value || 'Not provided',
            Phone_No: document.getElementById('phone')?.value || 'Not provided',
            email: document.getElementById('email')?.value || 'Not provided',
            Address: document.getElementById('street-address')?.value || 'Not provided',
            County: document.getElementById('state-province')?.value || 'Not provided',
            City: document.getElementById('city')?.value || 'Not provided',
            Post_Code: document.getElementById('zip-postal')?.value || 'Not provided',
            country_id: document.getElementById('country')?.value || 'Not provided',
            newsletter_opt_in: !!document.getElementById('newsletter-opt-in')?.checked
        };
    }

    async uploadFile(fileType) {
        const fileInput = document.getElementById(`${fileType}-files`);
        if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
            throw new Error(`No ${fileType} file selected`);
        }

        // Upload every file selected for this slot. For single-file slots this is
        // just one file; for multi-file slots (e.g. damage) the returned fileName
        // is a comma-separated string of all uploaded file URLs.
        const files = Array.from(fileInput.files);
        const uploads = await Promise.all(
            files.map(file => this.uploadSingleFile(file, fileType))
        );

        return { fileName: uploads.map(u => u.fileName).join(',') };
    }

    async uploadSingleFile(file, fileType) {
        const formData = new FormData();

        const uniqueId = `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        const lastDotIndex = file.name.lastIndexOf('.');
        const fileName = lastDotIndex !== -1
            ? `${file.name.substring(0, lastDotIndex)}_${uniqueId}${file.name.substring(lastDotIndex)}`
            : `${file.name}_${uniqueId}`;

        formData.append('image', file, fileName);

        const response = await fetch(this.apiEndpoints.AZURE_UPLOAD, {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            throw new Error(`${fileType} file upload failed: ${response.status}`);
        }

        return await response.json();
    }

    async registerWarrantyClaim(productData, receiptImageUrl, damageImageUrl, batchImageUrl) {
        // Never submit without an idempotency token — the backend requires it.
        const submissionToken = this.form.querySelector('input[name="submission-token"]')?.value;
        if (!submissionToken) {
            // Generic, customer-friendly message: refreshing re-runs init and re-tokenizes the form.
            throw new Error('Please refresh the page and try again.');
        }

        const body = {
            data: JSON.stringify(productData),
            batchImageUrl: batchImageUrl,
            receiptImageUrl: receiptImageUrl,
            damageImageUrl: damageImageUrl,
            submissionToken,
        };

        const response = await fetch(this.apiEndpoints.WARRANTY_REGISTER, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            throw new Error(`Warranty registration failed: ${response.status}`);
        }

        return await response.json();
    }

    setButtonState(button, disabled, content, additionalClass = null) {
        if (!button) return;
        button.disabled = disabled;
        if (Array.isArray(content)) {
            button.replaceChildren(...content.filter(Boolean));
        } else if (content instanceof Node) {
            button.replaceChildren(content);
        } else if (content === null || content === undefined) {
            button.replaceChildren();
        } else {
            button.textContent = String(content);
        }
        if (additionalClass) {
            button.classList.add(additionalClass);
        }
    }
}
